import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertCircle, RotateCcw, CheckCircle2, Plus, Trash2, FileVideo2, FileText } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, ShimmerButton, SimHeader } from "@/components/sim/shared";
import { NodeEditModal, type NodeDetail } from "@/components/neusim/NodeEditModal";
import { SimSettingsEditor } from "@/components/neusim/SimSettingsEditor";
import { AttemptsAdminCard } from "@/components/neusim/AttemptsAdminCard";
import { apiCall, ApiError } from "@/lib/api";

interface SimDetail {
  id: string;
  simCode: string;
  title: string;
  description: string | null;
  audience: string | null;
  difficulty: string | null;
  introText: string | null;
  aiSystemPrompt: string | null;
  cohortInsightsPrompt: string | null;
  projectId: string;
  projectName: string | null;
  competencyId: string | null;
  competencyName: string | null;
}

interface BandRow {
  id?: string;
  label: string;
  minScore: number;
  maxScore: number;
  displayOrder: number;
}

type NodeRow = NodeDetail;

interface ChoiceRow {
  id: string;
  nodeId: string;
  displayOrder: number;
  choiceText: string;
  score: number;
}

interface DetailResponse {
  sim: SimDetail;
  bands: Required<BandRow>[];
  nodes: NodeRow[];
  choices: ChoiceRow[];
  settings: { key: string; value: string }[];
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * AdminSimEditPage — /admin/sims/:id — sim metadata form + outcome bands
 * editor + read-only previews of nodes/choices/settings (those become
 * editable in Steps 8c-ii / 8c-iii).
 */
export function AdminSimEditPage(): JSX.Element {
  const { simId } = useParams<{ simId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state mirrors data.sim with optional patches.
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [introText, setIntroText] = useState("");
  const [aiSystemPrompt, setAiSystemPrompt] = useState("");
  const [cohortInsightsPrompt, setCohortInsightsPrompt] = useState("");
  const [defaultPromptLoading, setDefaultPromptLoading] = useState(false);
  const [bands, setBands] = useState<BandRow[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [projects, setProjects] = useState<Array<{ id: string; name: string; clientName: string; isActive: boolean }>>([]);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<NodeDetail | null>(null);
  const [adding, setAdding] = useState(false);
  const [addType, setAddType] = useState<"OpenText" | "MultipleChoice">("MultipleChoice");
  const [addTitle, setAddTitle] = useState("");
  const [addScenario, setAddScenario] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<NodeDetail | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleCreateNode = async (): Promise<void> => {
    if (data === null) return;
    if (addScenario.trim() === "") {
      setAddError("Scenario text is required");
      return;
    }
    setAddBusy(true);
    setAddError(null);
    try {
      await apiCall("/libraryNodeCreate", {
        method: "POST",
        body: {
          simId: data.sim.id,
          questionType: addType,
          scenarioText: addScenario,
          title: addTitle.trim() === "" ? null : addTitle,
        },
      });
      setAdding(false);
      setAddTitle("");
      setAddScenario("");
      setAddType("MultipleChoice");
      await load();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't create the node");
    } finally {
      setAddBusy(false);
    }
  };

  const handleDeleteNode = async (): Promise<void> => {
    if (deleting === null || data === null) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await apiCall("/libraryNodeDelete", {
        method: "POST",
        body: { simId: data.sim.id, nodeId: deleting.id },
      });
      setDeleting(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't delete the node");
    } finally {
      setDeleteBusy(false);
    }
  };

  const load = async (): Promise<void> => {
    if (simId === undefined) return;
    setLoading(true);
    setError(null);
    setSaveStatus("idle");
    setSaveError(null);
    try {
      const res = await apiCall<DetailResponse>("/libraryDetail", { method: "POST", body: { id: simId } });
      setData(res);
      setTitle(res.sim.title);
      setDescription(res.sim.description ?? "");
      setAudience(res.sim.audience ?? "");
      setDifficulty(res.sim.difficulty ?? "");
      setIntroText(res.sim.introText ?? "");
      setAiSystemPrompt(res.sim.aiSystemPrompt ?? "");
      setCohortInsightsPrompt(res.sim.cohortInsightsPrompt ?? "");
      setBands(res.bands.map((b) => ({ ...b })));
      setProjectId(res.sim.projectId);
      // Load the project picker list in parallel — we don't await so the
      // page renders fast; the picker shows "Loading…" briefly.
      void (async () => {
        try {
          const p = await apiCall<{ projects: Array<{ id: string; name: string; clientName: string; isActive: boolean }> }>("/libraryProjects", { method: "POST", body: {} });
          setProjects(p.projects);
        } catch { /* picker stays empty; current project name still shown above */ }
      })();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load this sim";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [simId]);

  const handleSave = async (): Promise<void> => {
    if (data === null) return;
    setSaveStatus("saving");
    setSaveError(null);
    try {
      // Validate bands client-side before sending — backend also validates.
      for (const b of bands) {
        if (b.label.trim() === "" || b.minScore < 0 || b.maxScore > 10 || b.minScore > b.maxScore) {
          throw new Error(`Invalid band: ${b.label || "(no label)"} (${b.minScore}-${b.maxScore})`);
        }
      }
      await apiCall("/librarySave", {
        method: "POST",
        body: {
          id: data.sim.id,
          fields: {
            title,
            description: description.trim() === "" ? null : description,
            audience: audience.trim() === "" ? null : audience,
            difficulty: difficulty.trim() === "" ? null : difficulty,
            introText: introText.trim() === "" ? null : introText,
            aiSystemPrompt: aiSystemPrompt.trim() === "" ? null : aiSystemPrompt,
            cohortInsightsPrompt: cohortInsightsPrompt.trim() === "" ? null : cohortInsightsPrompt,
            ...(projectId !== "" && projectId !== data.sim.projectId ? { projectId } : {}),
          },
          bands: bands.map((b, i) => ({
            id: b.id,
            label: b.label,
            minScore: b.minScore,
            maxScore: b.maxScore,
            displayOrder: i + 1,
          })),
        },
      });
      setSaveStatus("saved");
      await load(); // re-pull to confirm
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed";
      setSaveError(msg);
      setSaveStatus("error");
    }
  };

  const updateBand = (i: number, patch: Partial<BandRow>): void => {
    setBands((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };
  const addBand = (): void => {
    setBands((bs) => [...bs, { label: "New band", minScore: 0, maxScore: 10, displayOrder: bs.length + 1 }]);
  };
  const removeBand = (i: number): void => {
    setBands((bs) => bs.filter((_, idx) => idx !== i));
  };

  return (
    <ScreenShell>
      <SimHeader decision={data?.nodes.length ?? 0} xp={0} />
      <main className="mx-auto max-w-5xl px-4 pb-20 md:px-8">
        <button
          type="button"
          onClick={() => navigate("/admin/sims")}
          className="mb-5 inline-flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Sim Library
        </button>

        {loading && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} className="h-12 w-12 rounded-full border-2 border-white/10 border-t-fuchsia-400" />
            <p className="mt-4 text-sm text-white/55">Loading sim…</p>
          </div>
        )}

        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-10 max-w-lg p-10 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30 hover:bg-white/10"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Try again
            </button>
          </GlassCard>
        )}

        {!loading && error === null && data !== null && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300 font-mono">
                {data.sim.simCode}
              </div>
              <h1 className="mt-2 text-[28px] font-bold leading-tight">
                <GradientText>{data.sim.title}</GradientText>
              </h1>
              <p className="mt-1 text-sm text-white/55">
                {data.sim.projectName ?? "—"} · {data.sim.competencyName ?? "—"}
              </p>
            </div>

            {/* ── Metadata ── */}
            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">Metadata</h2>
              <div className="mt-4 space-y-4">
                <Field label="Title *">
                  <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" />
                </Field>
                <Field label="Description">
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" />
                </Field>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Audience"><input value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" /></Field>
                  <Field label="Difficulty">
                    <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none">
                      <option value="" className="bg-[#0c0e22]">—</option>
                      <option value="Easy" className="bg-[#0c0e22]">Easy</option>
                      <option value="Medium" className="bg-[#0c0e22]">Medium</option>
                      <option value="Hard" className="bg-[#0c0e22]">Hard</option>
                    </select>
                  </Field>
                </div>
                <Field label="Project (client + project this sim belongs to)">
                  <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none"
                  >
                    {projects.length === 0 && projectId !== "" && (
                      <option value={projectId} className="bg-[#0c0e22]">{data.sim.projectName ?? "Current project"} (loading…)</option>
                    )}
                    {projects.filter((p) => p.isActive || p.id === projectId).map((p) => (
                      <option key={p.id} value={p.id} className="bg-[#0c0e22]">
                        {p.clientName} · {p.name}{!p.isActive ? " (archived)" : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Intro text (markdown — drives the welcome briefing)">
                  <textarea value={introText} onChange={(e) => setIntroText(e.target.value)} rows={8} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-[12px] leading-relaxed text-white focus:border-fuchsia-400/60 focus:outline-none" />
                </Field>
                <Field label="AI coaching system prompt">
                  <textarea value={aiSystemPrompt} onChange={(e) => setAiSystemPrompt(e.target.value)} rows={10} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-[12px] leading-relaxed text-white focus:border-fuchsia-400/60 focus:outline-none" />
                </Field>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">
                      Cohort insights system prompt
                      <span className="ml-2 normal-case tracking-normal text-white/35">
                        {cohortInsightsPrompt.trim() === ""
                          ? "— blank means use the built-in default"
                          : "— customised for this sim"}
                      </span>
                    </label>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={async () => {
                          setDefaultPromptLoading(true);
                          try {
                            const res = await apiCall<{ prompt: string }>("/libraryDefaultCohortPrompt");
                            setCohortInsightsPrompt(res.prompt);
                          } catch (err) {
                            setSaveError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load default prompt");
                          } finally {
                            setDefaultPromptLoading(false);
                          }
                        }}
                        disabled={defaultPromptLoading}
                        className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30 disabled:opacity-40"
                        title="Load the built-in default into the editor"
                      >
                        {defaultPromptLoading ? "Loading…" : "Show default"}
                      </button>
                      {cohortInsightsPrompt.trim() !== "" && (
                        <button
                          type="button"
                          onClick={() => setCohortInsightsPrompt("")}
                          className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30"
                          title="Clear the override and revert to the built-in default at save time"
                        >
                          Clear (use default)
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    value={cohortInsightsPrompt}
                    onChange={(e) => setCohortInsightsPrompt(e.target.value)}
                    rows={12}
                    placeholder="Leave blank to use the built-in default. Click 'Show default' to load it here, then edit."
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-[12px] leading-relaxed text-white focus:border-fuchsia-400/60 focus:outline-none"
                  />
                  <p className="mt-1 text-[10.5px] text-white/40">
                    Sent to Claude as the system prompt when a facilitator clicks &ldquo;Generate insights&rdquo; on a cohort of this sim. The data payload (per-learner transcripts) is built by the runtime and is not editable here.
                  </p>
                </div>
              </div>
            </GlassCard>

            {/* ── Outcome bands ── */}
            <GlassCard className="p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-200">Outcome bands</h2>
                <button type="button" onClick={addBand} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/70 hover:border-white/25 hover:text-white">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              <p className="mt-1 text-[11px] text-white/40">Range 0-10 per decision; min ≤ max; spans should cover 0-10 without gaps.</p>
              <ul className="mt-3 space-y-2">
                {bands.map((b, i) => (
                  <li key={i} className="grid grid-cols-12 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <input value={b.label} onChange={(e) => updateBand(i, { label: e.target.value })} placeholder="Label" className="col-span-6 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" />
                    <input type="number" min={0} max={10} value={b.minScore} onChange={(e) => updateBand(i, { minScore: Number(e.target.value) })} className="col-span-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" />
                    <span className="col-span-1 text-center text-xs text-white/40">to</span>
                    <input type="number" min={0} max={10} value={b.maxScore} onChange={(e) => updateBand(i, { maxScore: Number(e.target.value) })} className="col-span-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white focus:border-fuchsia-400/60 focus:outline-none" />
                    <button type="button" onClick={() => removeBand(i)} className="col-span-1 text-white/40 transition-colors hover:text-rose-400" title="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </GlassCard>

            {/* ── Save bar ── */}
            <div className="sticky bottom-4 z-10 flex items-center justify-end gap-3">
              {saveStatus === "saved" && (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Saved
                </span>
              )}
              {saveError !== null && (
                <span className="inline-flex items-center gap-1.5 text-xs text-rose-300">
                  <AlertCircle className="h-3.5 w-3.5" /> {saveError}
                </span>
              )}
              <ShimmerButton onClick={() => void handleSave()}>
                {saveStatus === "saving" ? "Saving…" : "Save changes"}
              </ShimmerButton>
            </div>

            {/* ── Nodes (clickable → edit modal, with add + delete) ── */}
            <GlassCard className="p-6">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">
                  Decisions ({data.nodes.length}) <span className="text-white/30 normal-case tracking-normal">— click a row to edit (text, video/document, branching, choices &amp; scores)</span>
                </h2>
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-3 py-1 text-[11px] font-semibold text-fuchsia-100 transition-colors hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25"
                >
                  <Plus className="h-3 w-3" /> Add decision
                </button>
              </div>
              <ul className="mt-3 max-h-96 space-y-1.5 overflow-y-auto pr-1">
                {data.nodes.map((n) => {
                  const choices = data.choices.filter((c) => c.nodeId === n.id);
                  return (
                    <li
                      key={n.id}
                      onClick={() => setEditingNode(n)}
                      className="group cursor-pointer rounded-lg border border-white/10 bg-white/[0.02] p-2.5 transition-colors hover:border-fuchsia-400/30 hover:bg-white/[0.05]"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-sm font-medium text-white/90">
                          <span className="font-mono text-[10px] text-white/40">{n.nodeCode}</span> · {n.title ?? `Decision ${n.sequenceNo}`}
                        </p>
                        <div className="flex shrink-0 items-center gap-2 text-[11px] text-white/45">
                          {n.videoUrl !== null && <span title="Has video"><FileVideo2 className="h-3 w-3 text-fuchsia-300" /></span>}
                          {n.documentUrl !== null && <span title="Has document"><FileText className="h-3 w-3 text-cyan-300" /></span>}
                          <span>{n.questionType === "MultipleChoice" ? `MC · ${choices.length} choices` : "Open text"}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDeleting(n); setDeleteError(null); }}
                            className="ml-1 rounded p-1 text-white/30 opacity-0 transition-all hover:bg-rose-500/15 hover:text-rose-300 group-hover:opacity-100"
                            title={`Delete ${n.nodeCode}`}
                            aria-label={`Delete ${n.nodeCode}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </GlassCard>

            {/* ── Settings (editable) ── */}
            <GlassCard className="p-6">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">
                Settings <span className="text-white/30 normal-case tracking-normal">— 7 LIVE (runtime reads them), 21 COMING (saved for future wiring)</span>
              </h2>
              <div className="mt-4">
                <SimSettingsEditor simId={data.sim.id} initial={data.settings} onSaved={() => void load()} />
              </div>
            </GlassCard>

            {/* ── Attempts (per-user reset) ── */}
            <GlassCard className="p-6">
              <AttemptsAdminCard simId={data.sim.id} simCode={data.sim.simCode} />
            </GlassCard>
          </motion.div>
        )}

        {editingNode !== null && data !== null && (
          <NodeEditModal
            simId={data.sim.id}
            node={editingNode}
            allNodes={data.nodes}
            nodeChoices={data.choices.filter((c) => c.nodeId === editingNode.id)}
            onClose={() => setEditingNode(null)}
            onSaved={() => void load()}
          />
        )}

        {adding && (
          <div
            onClick={() => { if (!addBusy) setAdding(false); }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-10"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c0e22] to-[#070818] text-white shadow-2xl"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
              <div className="border-b border-white/10 px-6 py-4">
                <h2 className="text-lg font-bold">Add a decision</h2>
                <p className="mt-1 text-[12px] text-white/55">Appended to the end of the sim. You can rename, set branches, video and consequence after creation.</p>
              </div>
              <div className="space-y-4 px-6 py-5">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">Type</label>
                  <div className="mt-1.5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAddType("MultipleChoice")}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${addType === "MultipleChoice" ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-white" : "border-white/15 bg-white/5 text-white/65 hover:border-white/30"}`}
                    >
                      Multiple choice
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddType("OpenText")}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${addType === "OpenText" ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-white" : "border-white/15 bg-white/5 text-white/65 hover:border-white/30"}`}
                    >
                      Open text
                    </button>
                  </div>
                  <p className="mt-1 text-[10.5px] text-white/40">
                    {addType === "MultipleChoice" ? "Seeds 4 default options (A/B/C/D, score 5 each). Edit them after." : "Author the AI scoring rubric in the edit modal after creation."}
                  </p>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">Title (optional)</label>
                  <input
                    value={addTitle}
                    onChange={(e) => setAddTitle(e.target.value)}
                    placeholder="e.g. The Difficult Stakeholder"
                    className="mt-1.5 w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">Scenario text *</label>
                  <textarea
                    value={addScenario}
                    onChange={(e) => setAddScenario(e.target.value)}
                    rows={5}
                    placeholder="Second person. No obvious right answer. Set the stakes in the first 1–2 sentences."
                    className="mt-1.5 w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60"
                  />
                </div>
                {addError !== null && (
                  <div className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">{addError}</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-white/10 px-6 py-3">
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  disabled={addBusy}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreateNode()}
                  disabled={addBusy || addScenario.trim() === ""}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-1.5 text-[12px] font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" /> {addBusy ? "Creating…" : "Create decision"}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleting !== null && (
          <div
            onClick={() => { if (!deleteBusy) setDeleting(null); }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-2xl border border-rose-400/30 bg-gradient-to-br from-[#1a0a16] to-[#0c0810] text-white shadow-2xl"
            >
              <div className="border-b border-white/10 px-5 py-4">
                <h2 className="flex items-center gap-2 text-base font-bold text-rose-200">
                  <Trash2 className="h-4 w-4" /> Delete {deleting.nodeCode}?
                </h2>
              </div>
              <div className="px-5 py-4 text-[13px] leading-relaxed text-white/75">
                <p>
                  This removes <span className="font-mono text-rose-200">{deleting.nodeCode}</span> — {deleting.title ?? `Decision ${deleting.sequenceNo}`}.
                </p>
                <p className="mt-2 text-[12px] text-white/55">
                  Choices on this node delete with it. The action is rejected if another node branches into this one, if a learner is currently on it, or if any past attempt answered it.
                </p>
                {deleteError !== null && (
                  <div className="mt-3 rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">{deleteError}</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setDeleting(null)}
                  disabled={deleteBusy}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteNode()}
                  disabled={deleteBusy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/20 px-4 py-1.5 text-[12px] font-semibold text-rose-100 hover:border-rose-400/60 hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {deleteBusy ? "Deleting…" : "Delete decision"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </ScreenShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
